import 'swiper/css'

import {
  Badge,
  Box,
  Card,
  Flex,
  Grid,
  Heading,
  IconButton,
  Image,
  Input,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useMemo } from 'react'
import { LuPackage, LuSearch, LuX } from 'react-icons/lu'
import { Swiper, SwiperSlide } from 'swiper/react'

import { Button } from '@/components/ui/button'
import { useColorModeValue } from '@/components/ui/color-mode'
import { Field } from '@/components/ui/field'
import { getMaterialThumbnailImageFitProps } from '@/utils/image'

import type {
  CatalogPickerItem,
  MaterialCatalogSplitPickerProps,
} from './materialCatalogPickerTypes'

const MotionBox = motion.create(Box)

function CatalogItemCardBody({
  item,
  layout,
  headingSize,
}: {
  item: CatalogPickerItem
  layout: 'full' | 'compact'
  headingSize: 'sm' | 'md'
}) {
  const showMeta = layout === 'full'

  return (
    <Stack gap="2">
      <Heading size={headingSize} lineClamp={1}>
        {item.name}
      </Heading>
      {showMeta && item.category ? (
        <Badge colorPalette="purple" width="fit-content">
          {item.category.name}
        </Badge>
      ) : null}
      {(item.brand || item.model) && (
        <Text fontSize="sm" color="gray.600">
          {[item.brand, item.model].filter(Boolean).join(' - ')}
        </Text>
      )}
      {showMeta && item.description ? (
        <Text fontSize="sm" lineClamp={2} color="gray.500">
          {item.description}
        </Text>
      ) : null}
    </Stack>
  )
}

export const MaterialCatalogSplitPicker = ({
  helpBanner,
  search,
  catalogItems,
  loading,
  isInitialLoading = false,
  debouncedSearchTerm,
  selectionMode,
  selectedItems,
  onCatalogItemClick,
  onRemoveSelected,
  onClearSelection,
  strings,
  primaryAction,
  secondaryAction,
  useMobileBottomActionBar = true,
  singleSelectionFooter,
  previewOnlyTrailing,
  actionsTopContent,
  rightColumnVariant = 'default',
  desktopCatalogColumns = 3,
  catalogCardLayout = 'full',
}: MaterialCatalogSplitPickerProps) => {
  const cardBgColor = useColorModeValue('gray.50', 'gray.800')
  const selectedCardHoverBg = useColorModeValue('red.50', 'red.900')
  const mobileBottomBarBg = useColorModeValue('white', 'gray.900')
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const showActionBar = primaryAction != null || secondaryAction != null

  const swiperConfig = useMemo(
    () => ({
      spaceBetween: 16,
      slidesPerView: 1.2,
      breakpoints: {
        640: {
          slidesPerView: 2,
          spaceBetween: 16,
        },
      },
      touchStartPreventDefault: false,
      touchMoveStopPropagation: true,
      watchOverflow: true,
      resistance: true,
      resistanceRatio: 0.85,
    }),
    []
  )

  const selectionBadgeCount =
    selectionMode === 'single'
      ? selectedItems.length > 0
        ? 1
        : 0
      : selectedItems.length

  const previewOnlyRight =
    rightColumnVariant === 'previewOnly' && selectionMode === 'single'
  const selectedSingle = selectedItems[0]

  const actionButtons = showActionBar ? (
    <Stack gap="2" w="full">
      {primaryAction ? (
        <Button
          colorPalette="green"
          onClick={primaryAction.onClick}
          disabled={primaryAction.disabled}
          loading={primaryAction.loading}
          width="full"
        >
          {primaryAction.label}
        </Button>
      ) : null}
      {secondaryAction ? (
        <Button
          variant="outline"
          onClick={secondaryAction.onClick}
          width="full"
        >
          {secondaryAction.label}
        </Button>
      ) : null}
    </Stack>
  ) : null

  return (
    <>
      <Grid
        templateColumns={{ base: '1fr', lg: '2fr 1fr' }}
        gap="6"
        alignItems="start"
      >
        <Card.Root bg={cardBgColor} rounded="2xl">
          <Card.Body>
            <Stack gap="6">
              {helpBanner}

              <Field
                label={search.label}
                helperText={search.helperText}
                pos="relative"
              >
                <Input
                  size="lg"
                  placeholder={search.placeholder}
                  value={search.value}
                  onChange={(e) => search.onChange(e.target.value)}
                />
                <Box
                  position="absolute"
                  right="3"
                  top="50%"
                  transform="translateY(-50%)"
                  pointerEvents="none"
                >
                  <LuSearch />
                </Box>
              </Field>

              <Text fontWeight="medium" color="gray.600">
                {strings.availableCount(catalogItems.length)}
              </Text>

              {isInitialLoading ? (
                <Flex justify="center" py="12">
                  <Spinner size="xl" />
                </Flex>
              ) : catalogItems.length === 0 && debouncedSearchTerm ? (
                <Card.Root>
                  <Card.Body>
                    <Stack align="center" gap="4" py="8">
                      <LuPackage size={48} />
                      <Heading size="lg">{strings.emptySearchTitle}</Heading>
                      <Text color="gray.500" textAlign="center">
                        {strings.emptySearchDescription}
                      </Text>
                    </Stack>
                  </Card.Body>
                </Card.Root>
              ) : catalogItems.length === 0 &&
                !debouncedSearchTerm &&
                (selectionMode === 'multiple'
                  ? selectedItems.length > 0
                  : false) ? (
                <Card.Root>
                  <Card.Body>
                    <Stack align="center" gap="4" py="8">
                      <LuPackage size={48} />
                      <Heading size="lg">{strings.allSelectedTitle}</Heading>
                      <Text color="gray.500" textAlign="center">
                        {strings.allSelectedDescription}
                      </Text>
                    </Stack>
                  </Card.Body>
                </Card.Root>
              ) : isMobile ? (
                <Box>
                  <Swiper {...swiperConfig}>
                    {catalogItems.map((item) => (
                      <SwiperSlide key={item.id}>
                        <Card.Root
                          cursor="pointer"
                          onClick={() => onCatalogItemClick(item)}
                          _active={{ transform: 'scale(0.98)' }}
                          transition="all 0.2s"
                          h="full"
                        >
                          <Box
                            h="120px"
                            w="100%"
                            roundedTop="2xl"
                            overflow="hidden"
                          >
                            <Image
                              src={item.imageUrl}
                              alt={item.name}
                              h="100%"
                              w="100%"
                              bg="white"
                              {...getMaterialThumbnailImageFitProps(
                                item.imageUrl
                              )}
                            />
                          </Box>
                          <Card.Body>
                            <CatalogItemCardBody
                              item={item}
                              layout={catalogCardLayout}
                              headingSize="sm"
                            />
                          </Card.Body>
                        </Card.Root>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </Box>
              ) : (
                <SimpleGrid
                  columns={desktopCatalogColumns}
                  gap="4"
                  opacity={loading ? 0.6 : 1}
                  transition="opacity 0.2s"
                >
                  <AnimatePresence>
                    {catalogItems.map((item) => (
                      <MotionBox
                        key={item.id}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Card.Root
                          cursor="pointer"
                          onClick={() => onCatalogItemClick(item)}
                          _hover={{
                            shadow: 'lg',
                            transform: 'translateY(-4px)',
                          }}
                          transition="all 0.2s"
                          h="full"
                          borderTopRadius="3xl"
                        >
                          <Box
                            h="150px"
                            w="100%"
                            roundedTop="3xl"
                            overflow="hidden"
                          >
                            <Image
                              src={item.imageUrl}
                              alt={item.name}
                              h="100%"
                              w="100%"
                              bg={'white'}
                              {...getMaterialThumbnailImageFitProps(
                                item.imageUrl
                              )}
                            />
                          </Box>
                          <Card.Body>
                            <CatalogItemCardBody
                              item={item}
                              layout={catalogCardLayout}
                              headingSize="md"
                            />
                          </Card.Body>
                        </Card.Root>
                      </MotionBox>
                    ))}
                  </AnimatePresence>
                </SimpleGrid>
              )}
            </Stack>
          </Card.Body>
        </Card.Root>

        <Stack
          gap="4"
          position="sticky"
          top="4"
          display={{ base: 'none', lg: 'flex' }}
        >
          <Card.Root bg={cardBgColor} rounded="2xl">
            <Card.Body>
              <Stack gap="4">
                {previewOnlyRight ? (
                  <Stack gap="4" w="full">
                    {selectedItems.length === 0 ? (
                      <Stack align="center" py="8" gap="3">
                        <LuPackage size={32} color="gray" />
                        <Text color="gray.500" textAlign="center" fontSize="sm">
                          {strings.selectedEmpty}
                        </Text>
                      </Stack>
                    ) : (
                      <Stack gap="3" w="full">
                        {singleSelectionFooter}
                        {strings.removeSelection && selectedSingle ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            colorPalette="red"
                            alignSelf="flex-start"
                            onClick={() => onRemoveSelected(selectedSingle)}
                          >
                            {strings.removeSelection}
                          </Button>
                        ) : null}
                      </Stack>
                    )}
                    {previewOnlyTrailing}
                  </Stack>
                ) : (
                  <>
                    <Flex justify="space-between" align="center">
                      <Heading size="lg">{strings.selectedTitle}</Heading>
                      <Badge colorPalette="green" size="lg">
                        {selectionBadgeCount}
                      </Badge>
                    </Flex>

                    {selectedItems.length === 0 ? (
                      <Stack align="center" py="8" gap="3">
                        <LuPackage size={32} color="gray" />
                        <Text color="gray.500" textAlign="center" fontSize="sm">
                          {strings.selectedEmpty}
                        </Text>
                      </Stack>
                    ) : (
                      <Stack gap="2" maxH="50vh" overflowY="auto">
                        <AnimatePresence>
                          {selectedItems.map((item) => (
                            <MotionBox
                              key={item.id}
                              initial={{ opacity: 0, x: 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: -20 }}
                              transition={{ duration: 0.2 }}
                            >
                              <Card.Root
                                size="sm"
                                cursor="pointer"
                                onClick={() => onRemoveSelected(item)}
                                _hover={{
                                  bg: selectedCardHoverBg,
                                  borderColor: 'red.300',
                                }}
                                transition="all 0.2s"
                                borderWidth="1px"
                              >
                                <Card.Body p="3">
                                  <Flex
                                    justify="space-between"
                                    align="start"
                                    gap="2"
                                  >
                                    <Stack gap="1" flex="1">
                                      <Text
                                        fontWeight="medium"
                                        fontSize="sm"
                                        lineClamp={1}
                                      >
                                        {item.name}
                                      </Text>
                                      {catalogCardLayout === 'full' &&
                                      item.category ? (
                                        <Badge
                                          colorPalette="purple"
                                          size="xs"
                                          width="fit-content"
                                        >
                                          {item.category.name}
                                        </Badge>
                                      ) : null}
                                      {catalogCardLayout === 'compact' &&
                                      (item.brand || item.model) ? (
                                        <Text
                                          fontSize="xs"
                                          color="gray.600"
                                          lineClamp={1}
                                        >
                                          {[item.brand, item.model]
                                            .filter(Boolean)
                                            .join(' - ')}
                                        </Text>
                                      ) : null}
                                    </Stack>
                                    <IconButton
                                      aria-label="remove"
                                      size="xs"
                                      variant="ghost"
                                      colorPalette="red"
                                    >
                                      <LuX />
                                    </IconButton>
                                  </Flex>
                                </Card.Body>
                              </Card.Root>
                            </MotionBox>
                          ))}
                        </AnimatePresence>
                      </Stack>
                    )}

                    {selectionMode === 'multiple' &&
                    selectedItems.length > 0 &&
                    onClearSelection ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        colorPalette="red"
                        onClick={onClearSelection}
                      >
                        {strings.clearSelection}
                      </Button>
                    ) : null}

                    {selectionMode === 'single' &&
                      selectedItems.length > 0 &&
                      singleSelectionFooter}

                    {showActionBar ? (
                      <Stack gap="2" mt="4" pt="4" borderTopWidth="1px">
                        {actionsTopContent}
                        {actionButtons}
                      </Stack>
                    ) : null}
                  </>
                )}
              </Stack>
            </Card.Body>
          </Card.Root>
        </Stack>
      </Grid>

      <Box
        display={{ base: 'block', lg: 'none' }}
        position="sticky"
        top="0"
        zIndex="10"
        mb="4"
      >
        <Card.Root bg={cardBgColor} rounded="2xl" shadow="md">
          <Card.Body>
            <Stack gap="3">
              {previewOnlyRight ? (
                <Stack gap="4" w="full">
                  {selectedItems.length === 0 ? (
                    <Stack align="center" py="6" gap="3">
                      <LuPackage size={28} color="gray" />
                      <Text color="gray.500" textAlign="center" fontSize="sm">
                        {strings.selectedEmpty}
                      </Text>
                    </Stack>
                  ) : (
                    <Stack gap="3" w="full">
                      {singleSelectionFooter}
                      {strings.removeSelection && selectedSingle ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          colorPalette="red"
                          onClick={() => onRemoveSelected(selectedSingle)}
                        >
                          {strings.removeSelection}
                        </Button>
                      ) : null}
                    </Stack>
                  )}
                  {previewOnlyTrailing}
                </Stack>
              ) : (
                <>
                  <Flex justify="space-between" align="center">
                    <Heading size="md">{strings.selectedTitle}</Heading>
                    <Badge colorPalette="green" size="lg">
                      {selectionBadgeCount}
                    </Badge>
                  </Flex>

                  {selectedItems.length > 0 ? (
                    <Stack gap="2" maxH="30vh" overflowY="auto">
                      <AnimatePresence>
                        {selectedItems.map((item) => (
                          <MotionBox
                            key={item.id}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Card.Root
                              size="sm"
                              cursor="pointer"
                              onClick={() => onRemoveSelected(item)}
                              _hover={{
                                bg: selectedCardHoverBg,
                                borderColor: 'red.300',
                              }}
                              transition="all 0.2s"
                              borderWidth="1px"
                            >
                              <Card.Body p="2">
                                <Flex
                                  justify="space-between"
                                  align="center"
                                  gap="2"
                                >
                                  <Stack gap="1" flex="1">
                                    <Text
                                      fontWeight="medium"
                                      fontSize="xs"
                                      lineClamp={1}
                                    >
                                      {item.name}
                                    </Text>
                                    {catalogCardLayout === 'full' &&
                                    item.category ? (
                                      <Badge
                                        colorPalette="purple"
                                        size="xs"
                                        width="fit-content"
                                      >
                                        {item.category.name}
                                      </Badge>
                                    ) : null}
                                    {catalogCardLayout === 'compact' &&
                                    (item.brand || item.model) ? (
                                      <Text
                                        fontSize="xs"
                                        color="gray.600"
                                        lineClamp={1}
                                      >
                                        {[item.brand, item.model]
                                          .filter(Boolean)
                                          .join(' - ')}
                                      </Text>
                                    ) : null}
                                  </Stack>
                                  <IconButton
                                    aria-label="remove"
                                    size="xs"
                                    variant="ghost"
                                    colorPalette="red"
                                  >
                                    <LuX />
                                  </IconButton>
                                </Flex>
                              </Card.Body>
                            </Card.Root>
                          </MotionBox>
                        ))}
                      </AnimatePresence>
                    </Stack>
                  ) : null}

                  {selectionMode === 'single' &&
                    selectedItems.length > 0 &&
                    singleSelectionFooter}

                  {showActionBar && !useMobileBottomActionBar ? (
                    <Stack gap="2" pt="2">
                      {actionsTopContent}
                      {actionButtons}
                    </Stack>
                  ) : null}
                </>
              )}
            </Stack>
          </Card.Body>
        </Card.Root>
      </Box>

      {showActionBar && useMobileBottomActionBar ? (
        <Box
          display={{ base: 'block', lg: 'none' }}
          position="fixed"
          bottom="0"
          left="0"
          right="0"
          bg={mobileBottomBarBg}
          borderTopWidth="1px"
          p="4"
          shadow="lg"
          zIndex="9999"
        >
          <Stack gap="2">
            {actionsTopContent}
            <Flex gap="2">
              {secondaryAction ? (
                <Button
                  variant="outline"
                  onClick={secondaryAction.onClick}
                  flex="1"
                >
                  {secondaryAction.label}
                </Button>
              ) : null}
              {primaryAction ? (
                <Button
                  colorPalette="green"
                  onClick={primaryAction.onClick}
                  disabled={primaryAction.disabled}
                  loading={primaryAction.loading}
                  flex={secondaryAction ? '2' : '1'}
                >
                  {primaryAction.label}
                </Button>
              ) : null}
            </Flex>
          </Stack>
        </Box>
      ) : null}
    </>
  )
}
